import React, { useState } from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Image from '~/components/atoms/Image/Image'
import EmptyGrowthMessage from '~/components/blocks/EmptyGrowthMessage/EmptyGrowthMessage'
import GrowthStatus from '~/components/blocks/GrowthStatus/GrowthStatus'
import PortfolioItemDelete, {
  PortfolioDeleteIconType,
} from '~/components/blocks/PortfolioCollectionTableMenu/PortfolioItemDelete'
import { PortfolioCollectionVariant } from '~/components/blocks/PortfolioItemDetails/PortfolioItemDetails'
import useGeoLocation from '~/hooks/useLocation'
import usePortfolioInfoWindow from '~/hooks/usePortfolioInfoWindow'
import { getArtworkPrice } from '~/utils/getArtworkPrice'
import { getStringInitials } from '~/utils/getStringInitials'
import { renderMarketValue } from '~/utils/portfolioRenderers'

import type { ArtworkCardProps } from '../ArtworkCard/ArtworkCard'
import ArtworkPrice from '../ArtworkPrice/ArtworkPrice'
import AvailabilityIndicator from '../AvailabilityIndicator/AvailabilityIndicator'
import ImagePlaceholder from '../ImagePlaceholder/ImagePlaceholder'
import PortfolioCtas from '../PortfolioCtas/PortfolioCtas'
import { ExtendedTableContent } from '../Table/Table'
import Text, { TextSizes } from '../Text/Text'
import TicketLozenges, {
  TicketLozengeSizes,
} from '../TicketLozenges/TicketLozenges'

import styles from './PortfolioItemCard.module.css'

export interface PortfolioItemCardProps {
  artwork: ArtworkCardProps
  growth5Year: any
  growthSinceOwned?: any
  cta?: any
  inventoryPriority?: number
  item: any
  testID?: string
  hideDelete?: boolean
  variant?: any
}

export default function PortfolioItemCard({
  growth5Year,
  artwork,
  inventoryPriority,
  cta,
  testID,
  item,
  hideDelete = false,
  variant,
}: PortfolioItemCardProps) {
  const tPortfolio = useTranslations('portfolio')
  const { openInfoWindow } = usePortfolioInfoWindow()
  const { activeCurrencyIso } = useGeoLocation()
  const [expandedState, setExpandedState] = useState<boolean>(false)
  // need to know artwork price to conditionally set colours on available indicator
  const artworkPrice = getArtworkPrice(
    artwork.listPriceMin,
    artwork.listPriceMax,
    artwork.poa
  )

  const renderImage = () => {
    return (
      <div className={styles.imageContainer}>
        <div className={styles.imageContainerInner}>
          {(artwork.image && artwork.image.asset) || artwork.imageUrl ? (
            <Image
              asset={artwork.imageUrl ? artwork.imageUrl : artwork.image.asset}
              alt={`${artwork.title} - ${artwork.format}`}
              objectFit="contain"
            />
          ) : (
            <ImagePlaceholder text={getStringInitials(artwork.artist || '')} />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.base} data-testid={testID}>
      {!hideDelete && item && (
        <div className={styles.deleteBtnWrap}>
          <PortfolioItemDelete
            variant={PortfolioDeleteIconType.cross}
            item={item}
          />
        </div>
      )}

      {artwork.agnostic ? (
        renderImage()
      ) : (
        <div
          className={styles.clickable}
          onClick={() => openInfoWindow(item, variant, undefined, false)}
        >
          {renderImage()}
        </div>
      )}

      <div
        className={styles.details}
        onClick={() => openInfoWindow(item, variant, undefined, false)}
      >
        <Text
          size={TextSizes.secondaryTitleLrg}
          italic={true}
          className={styles.title}
          Tag="h4"
        >
          {artwork.title}&nbsp;
        </Text>
        {artwork.format && (
          <Text size={TextSizes.bodySml} className={styles.format} uppercase>
            {artwork.format}&nbsp;
          </Text>
        )}

        <Text
          size={TextSizes.primaryTitleSml}
          className={styles.artist}
          Tag="h5"
        >
          {artwork.artist}
        </Text>
      </div>

      {artwork.agnostic ? (
        <>
          {item?.marketValue ? (
            <div>
              <Text size={TextSizes.bodySml}>
                {tPortfolio('myEstimatedValue')}
              </Text>
              {renderMarketValue(item.marketValue, activeCurrencyIso || '')}
            </div>
          ) : null}
        </>
      ) : (
        <ArtworkPrice
          listPriceMin={artwork.listPriceMin}
          listPriceMax={artwork.listPriceMax}
          listPriceMinValues={artwork.listPriceMinValues}
          listPriceMaxValues={artwork.listPriceMaxValues}
          poa={artwork.poa}
          pretitle="Price: "
          poaPlaceHolder={'data unavailable'}
        />
      )}

      {growth5Year ? (
        <div
          className={classNames(styles.growthWrap, {
            [styles.clickable]: growth5Year === null,
          })}
          onClick={() =>
            growth5Year === null && setExpandedState(!expandedState)
          }
        >
          <Text size={TextSizes.bodySml}>{tPortfolio('growth5Years')}</Text>
          {/* Check if growth5Year is not a reactNode */}
          {!React.isValidElement(growth5Year) ? (
            <GrowthStatus size="small" growth={+growth5Year} monochrome />
          ) : (
            <div className={styles.growth5YearComponent}>{growth5Year}</div>
          )}
        </div>
      ) : (
        <div
          className={classNames(styles.growthWrap, {
            [styles.clickable]: growth5Year === null,
          })}
          onClick={() => setExpandedState(!expandedState)}
        >
          <Text size={TextSizes.bodySml}>{tPortfolio('growth5Years')}</Text>

          <GrowthStatus
            size="medium"
            growth={undefined}
            graphGrey
            showInfoIcon
            showEmptyText
          />
        </div>
      )}
      {expandedState && (
        <div className={styles.expandedContent}>
          <ExtendedTableContent
            content={<EmptyGrowthMessage />}
            setExpandedState={() => setExpandedState(false)}
          />
        </div>
      )}
      {
        inventoryPriority && inventoryPriority > 0 ? (
          <AvailabilityIndicator
            showTooltip={false}
            isListing={artworkPrice === 'POA'}
            inventoryPriority={inventoryPriority}
          />
        ) : null
        // <div className={styles.noPrioritySpacer} />
      }

      <div className={styles.tradingFloorLozenges}>
        <TicketLozenges
          buyTicketsCount={item.artwork.buyTicketsCount}
          sellTicketsCount={item.artwork.sellTicketsCount}
          ownedTicketsCount={item.artwork.ownedTicketsCount}
          size={TicketLozengeSizes.small}
          borderTop
          paddingTop={false}
        />
      </div>

      <div className={styles.ctas}>
        {cta ? (
          <div className={styles.ctaComponent}>{cta}</div>
        ) : (
          item && (
            <PortfolioCtas
              type={PortfolioCollectionVariant.Watching}
              smallFont
              item={item}
              noMinWidth
            />
          )
        )}
      </div>
    </div>
  )
}
